import React, { useEffect, useState, useCallback } from 'react';
import AthleteAttendanceTable from './AthleteAttendanceTable';
import PropTypes from 'prop-types';
import LoadingContent from 'components/NewDesign/LoadingContent';
import { toast } from 'react-toastify';
import { getAthleteAttendanceReport } from 'api/reportsApi';

const AthleteAttendance = ({ clubID, onDataChange }) => {
  const storageKey = `athleteAttendanceFilters_${clubID}`;

  // Load saved filters from SessionStorage
  const getSavedFilters = () => {
    try {
      const saved = sessionStorage.getItem(storageKey);
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  };

  const savedFilters = getSavedFilters();

  const [athleteAttendance, setAthleteAttendance] = useState([]);
  const [filteredAttendance, setFilteredAttendance] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [minAttendancePercent, setMinAttendancePercent] = useState(savedFilters?.minAttendancePercent ?? 50);
  const [loading, setLoading] = useState(true);
  const [totalMeets, setTotalMeets] = useState(0);
  const [seasonName, setSeasonName] = useState('');

  // Save filters to SessionStorage whenever they change
  useEffect(() => {
    const filters = {
      minAttendancePercent,
    };
    sessionStorage.setItem(storageKey, JSON.stringify(filters));
  }, [minAttendancePercent, storageKey]);

  const fetchAthleteAttendance = useCallback(async () => {
    try {
      if (clubID) {
        setLoading(true);
        const response = await getAthleteAttendanceReport(clubID);
        const data = response.data || {};
        const attendanceData = data.athletes || [];
        setAthleteAttendance(attendanceData);
        setTotalMeets(data.totalClubMeets || 0);
        setSeasonName(data.season?.seasonName || '');
      }
      setLoading(false);
    } catch (err) {
      console.error('[AthleteAttendance] Fetch athlete attendance error: ', err);
      toast.error('Failed to load athlete attendance report');
      setLoading(false);
    }
  }, [clubID]);

  useEffect(() => {
    if (clubID) {
      fetchAthleteAttendance();
    }
  }, [fetchAthleteAttendance, clubID]);

  useEffect(() => {
    if (athleteAttendance?.length) {
      let results = athleteAttendance;

      // Apply search filter
      if (searchText) {
        results = results.filter((p) => {
          const fullName = `${p.firstName} ${p.lastName}`.toLowerCase();
          return fullName.includes(searchText.toLowerCase());
        });
      }

      // Apply minimum attendance percent filter
      if (minAttendancePercent > 0) {
        results = results.filter((p) => p.attendancePercentage >= minAttendancePercent);
      }

      setFilteredAttendance(results);
    } else {
      setFilteredAttendance([]);
    }
  }, [searchText, athleteAttendance, minAttendancePercent]);

  // Notify parent component of data changes (for export button)
  useEffect(() => {
    if (onDataChange) {
      onDataChange({
        hasData: filteredAttendance.length > 0,
        seasonName,
      });
    }
  }, [filteredAttendance, seasonName, onDataChange]);

  return (
    <>
      <LoadingContent loading={loading}>
        <AthleteAttendanceTable
          athleteAttendance={filteredAttendance}
          setSearchText={setSearchText}
          minAttendancePercent={minAttendancePercent}
          setMinAttendancePercent={setMinAttendancePercent}
          totalMeets={totalMeets}
        />
      </LoadingContent>
    </>
  );
};

AthleteAttendance.propTypes = {
  clubID: PropTypes.string,
  onDataChange: PropTypes.func,
};

export default AthleteAttendance;
